import { ButtonPrimary } from "components/Atoms/Button";
import React, { useCallback } from "react";
import { routes } from "utilities/routing/generatedRoutes";
import { PreviewPanel, PreviewPanelContent, PreviewPanelFooter, PreviewPanelHeader, useOpenPreviewPanel } from "../PreviewPanel";
import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import { useApi } from "utilities/hookutils";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { GoogleMapLocation } from "components/GoogleMap/GoogleMapLocation";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";

interface AirportPreviewPanelProps {
  airportId: number;
}

const AirportPreviewPanel = (props: AirportPreviewPanelProps) => {
  const { airportId } = props;

  const api = useApi(() => window.CoreApi.Airport.GetAirport(airportId), ["airport", airportId], {
    startEnabled: true
  });

  React.useEffect(() => {
    api.refetch();
  }, [airportId]);

  if (api.isInitialLoading) {
    return <>
      <PreviewPanelHeader title={RESX.Flight.Airport} />
      <PreviewPanelContent>
        <PlaceholderContent />
      </PreviewPanelContent>
      <PreviewPanelFooter />
    </>
  }

  const airport = api.data.data;
  const localDateFormatter = Intl.DateTimeFormat(site.culture.uiLocale, { timeZone: airport.timeZoneRegionName, hour: "2-digit", minute: "2-digit" });
  return <>
    <PreviewPanelHeader title={RESX.Flight.Airport} />
    <PreviewPanelContent>
      <div className="flex-column gap-larger">

        <PreviewPanelDetailRow>
          <div>{airport.name}</div>
          <div>{airport.location}</div>
        </PreviewPanelDetailRow>

        {airport.iata && <PreviewPanelDetailRow labelTop="IATA">
          {airport.iata}
        </PreviewPanelDetailRow>}

        {airport.icao && <PreviewPanelDetailRow labelTop="ICAO">
          {airport.icao}
        </PreviewPanelDetailRow>}

        <PreviewPanelDetailRow labelTop={RESX.DateTime.LocalTime}>
          {localDateFormatter.format(new Date())}
          <div className="text-sublabel">{airport.timeZoneRegionName}</div>
        </PreviewPanelDetailRow>

        <ViewOnMap height={400}>
          <GoogleMapLocation pin={{ type: "airport" }} location={airport.point} />
        </ViewOnMap>
      </div>
    </PreviewPanelContent >
    <PreviewPanelFooter>
      <ButtonPrimary href={routes.airport_Detail(airport.airportId).absolute} label={RESX.Buttons.DetailView} />
    </PreviewPanelFooter>
  </>
}

export interface AirportPreviewProps {
  airportId: number;
  trigger: JSX.Element
  onOpen?: () => void
}

export const AirportPreview = (props: AirportPreviewProps) => {
  return <PreviewPanel
    onOpen={props.onOpen}
    trigger={props.trigger}
    size="medium"
    id={`airport-preview-${props.airportId}`}
  >
    <AirportPreviewPanel airportId={props.airportId} />
  </PreviewPanel>
}

export const useOpenAirportPreviewPanel = () => {
  const openPreviewPanel = useOpenPreviewPanel();

  const openAirportPreviewPanel = useCallback((airportId: number) => {
    openPreviewPanel(<AirportPreviewPanel airportId={airportId} />, `aiport-preview-${airportId}`);
  }, [openPreviewPanel]);

  return openAirportPreviewPanel;
}